/**
 * Echo Hunt -- the Worlds tab (P8, §8.1 + §8.5).
 *
 * Split out of the Settings tab so that tab stays a list of GAME settings rather than a pile of
 * every editor in the feature. This tab owns the two things that describe where a story happens:
 *
 *  1. **The world pool** (§8.5). Seeded automatically on first mount, then editable -- a bible written
 *     once is not automatically right for the user's dreams, and the weaver obeys it literally.
 *  2. **The schematic rules** (§8.1, invariant #8). The user's own in-dream regularities, which the
 *     weave must not break. They live here because they are properties of the SETTING (what this world
 *     allows, how travel works in it) rather than of the game's difficulty -- a rule that the story
 *     violates teaches the wrong schema, which is a world-correctness bug, not a scoring one.
 *
 * ## Why worlds tolerate absurdity (§8.5)
 *
 * The frame (§8.2) already makes bizarreness canonical, but a world that CANNOT absorb a wild event
 * forces the weaver to smooth it into something ordinary -- and smoothing is exactly how a real dream
 * bullet gets eroded (§17). So the pool favours tolerant worlds, and the tolerance is a field on each
 * world so the weaver can be told about it instead of guessing.
 */
import React, {useState} from "react";
import {observer_mgl} from "mobx-graphlink";
import {Button, CheckBox, Column, Row, Text, TextArea, TextInput} from "react-vcomponents";
import {ES, InfoButton} from "web-vcore";
import {ScrollView} from "react-vscrollview";
import {GetMyEchoProfile} from "../../../EchoHunt/Profiles_Live.js";
import {
	GetWorldPool_Live,
	RemoveWorld_Live,
	SetSchematicRules_Live,
	SetWorldPool_Live,
	UpsertWorld_Live,
} from "../../../EchoHunt/Weave_Live.js";
import {EchoWorld} from "../../../EchoHunt/Types.js";
import {DEFAULT_SCHEMATIC_RULES, GetSeedWorlds} from "../../../EchoHunt/Worlds.js";
import {NewWorld, ApplyWorldDraft, ClampTolerance} from "../../../EchoHunt/Lore.js";
import {DraftWorld_Live} from "../../../EchoHunt/Characters_Live.js";
import {LoreWorldField} from "../../../EchoHunt/Prompts/Lore.js";
import type {WorldDraftKey} from "../../../EchoHunt/Prompts/Lore.js";

export const WorldsPanel = observer_mgl(()=>{
	const profile = GetMyEchoProfile();

	return (
		<ScrollView style={ES({flex: 1, padding: 10})} contentStyle={{display: "flex", flexDirection: "column"}}>
			<Text>Echo Hunt -- Worlds</Text>
			{profile == null
				? <Text mt={10} style={{opacity: .7}}>Create an Echo Hunt profile to edit the world pool.</Text>
				: <Column mt={10} style={{gap: 14}}>
					<WorldPoolEditor/>
					<SchematicRulesPanel/>
				</Column>}
		</ScrollView>
	);
});

// the world pool (§8.5)
// ==========

const WorldPoolEditor = observer_mgl(()=>{
	const worldPool = GetWorldPool_Live();
	const [expandedWorldId, setExpandedWorldId] = useState<string|null>(null);
	const [busy, setBusy] = useState(false);
	// a world being added: null = the add form is closed. §8.5 calls generated worlds an experiment, and
	// §8.7's flow is what makes one usable without writing a 200-word bible by hand.
	const [newWorld, setNewWorld] = useState<EchoWorld|null>(null);

	return (
		<Column style={{gap: 4}}>
			<Row>
				<Text style={{fontWeight: "bold"}}>World pool</Text>
				<InfoButton ml={5} text={[
					"§8.5: the worlds a story can be dropped into.",
					"",
					"The pool favours worlds that TOLERATE absurdity. A world that cannot absorb a wild event forces the",
					"weaver to smooth it into something ordinary, and smoothing is exactly how a real dream bullet gets",
					"eroded (§17).",
					"",
					"Two exclusions are deliberate: no world that is ABOUT dreams (they would blur the label in-story -- a",
					"character walking through a dream-world is not obviously wrong to think they are awake), and no world",
					"the user has no feel for.",
					"",
					"Bibles are constraints the weaver obeys literally: what exists here, what is ordinary, what is",
					"impossible, and what must not be contradicted. Editing one changes every future round in that world.",
				].join("\n")}/>
			</Row>

			<Text style={{opacity: .8}}>{`${worldPool.length} world(s)`}</Text>
			{worldPool.length == 0 &&
				<Text style={{color: "orange"}}>
					No worlds yet -- they are seeded automatically; if this persists, the profile write failed.
				</Text>}

			{worldPool.map(entry=>
				<WorldRow
					key={entry.id}
					world={entry}
					expanded={expandedWorldId == entry.id}
					busy={busy}
					onToggle={()=>setExpandedWorldId(expandedWorldId == entry.id ? null : entry.id)}
					setBusy={setBusy}/>)}

			{newWorld != null &&
				<NewWorldEditor
					key={newWorld.id}
					world={newWorld}
					busy={busy}
					setBusy={setBusy}
					onDone={()=>setNewWorld(null)}/>}

			<Row mt={4} style={{gap: 6, flexWrap: "wrap"}}>
				<Button text="Add world" enabled={!busy && newWorld == null} onClick={()=>setNewWorld(NewWorld())}/>
				<Button text="Restore the seeded worlds" enabled={!busy} onClick={async()=>{
					setBusy(true);
					// a UNION, not a replace: a user-added world must not be silently deleted by pressing this
					const existingIds = new Set(worldPool.map(a=>a.id));
					const restored = [...worldPool, ...GetSeedWorlds().filter(a=>!existingIds.has(a.id))];
					await SetWorldPool_Live(restored);
					setBusy(false);
				}}/>
			</Row>
		</Column>
	);
});

const WorldRow = observer_mgl((props: {
	world: EchoWorld,
	expanded: boolean,
	busy: boolean,
	onToggle: ()=>void,
	setBusy: (value: boolean)=>void,
})=>{
	const {world, expanded, busy, onToggle, setBusy} = props;
	const [name, setName] = useState(world.name);
	const [bible, setBible] = useState(world.bible);
	const [tolerance, setTolerance] = useState(`${world.crazinessTolerance}`);
	const worldPool = GetWorldPool_Live();

	return (
		<Column style={{padding: 6, borderRadius: 4, background: "rgba(255,255,255,.04)"}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold"}}>{world.name}</Text>
				<Text style={{fontSize: 11, opacity: .6}}>{`craziness tolerance: ${world.crazinessTolerance}`}</Text>
				<Text style={{fontSize: 11, opacity: .5}}>{`${world.bible.split(/\s+/).length} words`}</Text>
				<Button text={expanded ? "Collapse" : "Edit"} onClick={onToggle}/>
				<Button text="Remove" enabled={!busy && worldPool.length > 1} onClick={async()=>{
					setBusy(true);
					// refuses to remove the last world: a story needs somewhere to be
					await RemoveWorld_Live(world.id);
					setBusy(false);
				}}/>
			</Row>

			{!expanded &&
				<Text style={{marginTop: 3, fontSize: 12, opacity: .7}}>{world.bible.substring(0, 200)}{world.bible.length > 200 ? "…" : ""}</Text>}

			{expanded &&
				<Column mt={4} style={{gap: 4}}>
					<Row center style={{gap: 6}}>
						<Text style={{width: 60}}>Name</Text>
						<TextInput width={260} value={name} onChange={setName}/>
					</Row>
					<Row center style={{gap: 6}}>
						<Text style={{width: 60}}>Tolerance</Text>
						<TextInput width={80} value={tolerance} onChange={setTolerance} placeholder="0..1"/>
						<Text style={{fontSize: 11, opacity: .6}}>0 = nearly ordinary; 1 = anything can happen</Text>
					</Row>
					<Row center style={{gap: 6, alignItems: "flex-start"}}>
						<Text style={{width: 60}}>Bible</Text>
						<TextArea value={bible} onChange={setBible} style={{width: 520, height: 140, resize: "vertical"}}/>
					</Row>
					<Row style={{gap: 6}}>
						<Button text="Save" enabled={!busy} onClick={async()=>{
							setBusy(true);
							const parsedTolerance = Number(tolerance);
							await UpsertWorld_Live({
								...world,
								name: name.trim().length > 0 ? name.trim() : world.name,
								bible: bible.trim().length > 0 ? bible.trim() : world.bible,
								crazinessTolerance: Number.isFinite(parsedTolerance) ? Math.max(0, Math.min(1, parsedTolerance)) : world.crazinessTolerance,
							});
							setBusy(false);
						}}/>
						<Button text="Reset" onClick={()=>{
							setName(world.name);
							setBible(world.bible);
							setTolerance(`${world.crazinessTolerance}`);
						}}/>
					</Row>
					<Text style={{fontSize: 11, opacity: .55}}>
						Keep the bible as CONSTRAINTS the weaver can obey: what exists, what is ordinary, what is impossible,
						and what must not be contradicted. Do not write a plot summary -- it invites the weaver to write
						scenes from the summary instead of from the bullets.
					</Text>
				</Column>}
		</Column>
	);
});

// new world (§8.5, §8.7)
// ==========

/** The Worlds tab's "Add world" form (§8.7's flow, applied to worlds).
 *
 * Three ways in, in increasing order of effort: press "Draft all" for a whole generated world, type a
 * hint first for a steered one, or just write the bible yourself. The draft path is the same one the
 * Characters tab uses, so both share the `loreGenerator` model setting and the same visibility rule
 * (no journal material reaches the authoring prompt).
 *
 * Save is disabled until a name and a bible exist: a world with an empty bible would make the weaver
 * guess what the place is, which is exactly the asymmetry §8.5's tolerance field exists to avoid. */
const NewWorldEditor = observer_mgl((props: {
	world: EchoWorld,
	busy: boolean,
	setBusy: (value: boolean)=>void,
	onDone: ()=>void,
})=>{
	const {busy, setBusy, onDone} = props;

	const [draft, setDraft] = useState<EchoWorld>(props.world);
	const [hint, setHint] = useState("");
	const [override, setOverride] = useState(false);
	const [busyField, setBusyField] = useState<LoreWorldField|null>(null);
	const [message, setMessage] = useState<string|null>(null);

	const change = (updates: Partial<EchoWorld>)=>{
		setDraft({...draft, ...updates});
	};

	/** Drafts one field and merges ONLY that field into the local draft (`ApplyWorldDraft` enforces it,
	 * so a stray rewrite of the bible cannot land while the user asked for a name). Nothing is persisted
	 * until Save. */
	const draftField = async(field: LoreWorldField)=>{
		setBusyField(field);
		setMessage(null);
		try {
			const result = await DraftWorld_Live({base: draft, field, override, hint});
			const merged = ApplyWorldDraft(draft, result.draft, result.keys as WorldDraftKey[]);
			setDraft({...merged, id: draft.id, generated: merged.generated});
			if (result.keys.length == 0) {
				setMessage("Every field is already filled in. Tick Override to redraft them anyway.");
			} else if (!result.generatedByModel) {
				setMessage(result.error != null
					? `Used the offline generator (${result.error})`
					: "Used the offline generator -- no model returned a draft.");
			}
		} catch (ex) {
			setMessage(`Drafting failed: ${ex}`);
		}
		setBusyField(null);
	};

	const drafting = busyField != null;
	const canSave = draft.name.trim().length > 0 && draft.bible.trim().length > 0;

	return (
		<Column style={{gap: 5, padding: 8, borderRadius: 4, background: "rgba(255,255,255,.07)"}}>
			<Row center style={{gap: 6, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold"}}>New world</Text>
				<Button text={busyField == "all" ? "Drafting…" : "Draft all fields"} enabled={!drafting}
					onClick={()=>draftField("all")}/>
				<CheckBox text="Override" value={override} onChange={setOverride}
					title="Redraft EVERY field, replacing what is already there. Without this, a draft only fills empty fields."/>
				<TextInput style={{width: 340}} value={hint} onChange={setHint}
					placeholder="optional steer, e.g. a flooded underground shopping arcade"/>
			</Row>

			<Row center style={{gap: 6}}>
				<Text style={{width: 60}}>Name</Text>
				<TextInput width={260} value={draft.name} onChange={value=>change({name: value})}/>
				<Button text="Draft" enabled={!drafting} onClick={()=>draftField("name")}/>
			</Row>
			<Row center style={{gap: 6}}>
				<Text style={{width: 60}}>Tolerance</Text>
				<TextInput width={80} value={`${draft.crazinessTolerance}`} onChange={value=>{
					const parsed = Number(value);
					change({crazinessTolerance: Number.isFinite(parsed) ? parsed : draft.crazinessTolerance});
				}} placeholder="0..1"/>
				<Button text="Draft" enabled={!drafting} onClick={()=>draftField("tolerance")}/>
				<Text style={{fontSize: 11, opacity: .6}}>0 = nearly ordinary; 1 = anything can happen</Text>
			</Row>
			<Row center style={{gap: 6, alignItems: "flex-start"}}>
				<Text style={{width: 60}}>Bible</Text>
				<TextArea value={draft.bible} onChange={value=>change({bible: value})}
					style={{width: 520, height: 130, resize: "vertical"}}/>
				<Button text="Draft" enabled={!drafting} onClick={()=>draftField("bible")}/>
			</Row>

			{message != null && <Text style={{fontSize: 11, color: "orange"}}>{message}</Text>}

			<Row style={{gap: 6}}>
				<Button text="Add to pool" enabled={!busy && !drafting && canSave}
					title={canSave ? undefined : "A name and a bible are required."}
					onClick={async()=>{
						setBusy(true);
						await UpsertWorld_Live({
							...draft,
							name: draft.name.trim(),
							bible: draft.bible.trim(),
							crazinessTolerance: ClampTolerance(draft.crazinessTolerance),
						});
						setBusy(false);
						onDone();
					}}/>
				<Button text="Cancel" enabled={!busy && !drafting} onClick={onDone}/>
			</Row>
			<Text style={{fontSize: 11, opacity: .55}}>
				A bible is CONSTRAINTS the weaver can obey: what exists, what is ordinary, what is impossible, and what
				must not be contradicted. Never a plot summary -- it invites scenes written from the summary instead of
				from the bullets.
			</Text>
		</Column>
	);
});

// schematic rules (§8.1)
// ==========

const SchematicRulesPanel = observer_mgl(()=>{
	const profile = GetMyEchoProfile();
	const [text, setText] = useState<string|null>(null);
	const [busy, setBusy] = useState(false);

	if (profile == null) return null;

	const rules = profile.schematicRules ?? [];
	// the textarea starts as the stored rules, and only switches to the local draft once edited
	const draft = text ?? rules.join("\n");

	return (
		<Column style={{gap: 4}}>
			<Row>
				<Text style={{fontWeight: "bold"}}>Schematic rules</Text>
				<InfoButton ml={5} text={[
					"§8.1, invariant #8: your own in-dream REGULARITIES, which the weave must not break.",
					"",
					"Each line is a hard constraint handed to the weaver. The point is that a story violating one of these",
					"teaches the wrong schema -- you would be training on dream content that is wrong about your own dreams.",
					"",
					"One rule per line. An empty list is a legitimate choice: it just means the weaver is told no extra",
					"constraints beyond the world and the character.",
				].join("\n")}/>
			</Row>
			<TextArea
				value={draft} style={{width: 560, height: 110, resize: "vertical"}}
				onChange={setText}
				placeholder={DEFAULT_SCHEMATIC_RULES.join("\n")}/>
			<Row style={{gap: 6}}>
				<Button text="Save" enabled={!busy} onClick={async()=>{
					setBusy(true);
					const parsed = draft.split("\n").map(a=>a.trim()).filter(a=>a.length > 0);
					await SetSchematicRules_Live(parsed);
					setText(null); // fall back to the stored value
					setBusy(false);
				}}/>
				<Button text="Reset to the seeded rules" enabled={!busy} onClick={async()=>{
					setBusy(true);
					await SetSchematicRules_Live([...DEFAULT_SCHEMATIC_RULES]);
					setText(null);
					setBusy(false);
				}}/>
			</Row>
		</Column>
	);
});
